import { Toast } from '@yoast/ui-library';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

export interface PopoverProps {
  id: string;
  isVisible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  variant?: 'info' | 'success' | 'warning' | 'danger';
  showArrow?: boolean;
  keepOpenOnExternalClick?: boolean;
  delay?: number;
}

const Popover: React.FC<PopoverProps> = ({
  id,
  isVisible,
  onClose,
  children,
  className,
  variant = 'info',
  showArrow = true,
  keepOpenOnExternalClick = false,
  delay = 0,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [hasAnimationStarted, setHasAnimationStarted] = useState(false);

  // Handle the animation delay
  useEffect(() => {
    // If not visible, avoid unnecessary state updates
    if (!isVisible) {
      if (isContentVisible || hasAnimationStarted) {
        setIsContentVisible(false);
        setHasAnimationStarted(false);
      }
      return;
    }

    if (isVisible) {
      if (delay > 0) {
        // Reset animation state
        setHasAnimationStarted(false);
        setIsContentVisible(false);

        const timer = setTimeout(() => {
          setIsContentVisible(true);
          // Trigger animation slightly after showing to ensure DOM is ready
          setTimeout(() => setHasAnimationStarted(true), 50);
        }, delay);
        return () => clearTimeout(timer);
      } else {
        // Show immediately if no delay
        setIsContentVisible(true);
        setHasAnimationStarted(false);
      }
    }
    // disable eslint for this line because this effect manages those states
    // internally and already guards updates when !isVisible. Keeping the
    // deps tight to [isVisible, delay] avoids re-run loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, delay]);

  // Handle clicking outside to close popover
  useEffect(() => {
    if (!keepOpenOnExternalClick) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      if (isVisible) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isVisible, onClose, keepOpenOnExternalClick]);

  if (!isVisible) return null;

  return (
    <div id={id} className="yst-absolute yst-w-80" ref={popoverRef}>
      {/* Arrow pointing up */}
      {isContentVisible && showArrow && (
        <div
          className={cx(
            'yst-absolute yst-left-1/2 yst-top-full yst-z-[60] yst-mt-[7px] yst-h-3 yst-w-3 yst--translate-x-1/2 yst--translate-y-1/2 yst-rotate-45 yst-transform yst-border-l yst-border-t yst-border-gray-100 yst-bg-white',
            {
              'yst-opacity-0': delay > 0 && !hasAnimationStarted,
              'yst-animate-in yst-fade-in yst-slide-in-from-top-1 yst-duration-300':
                delay > 0 && hasAnimationStarted,
            },
          )}
        />
      )}
      <Toast
        id={`${id}--toast`}
        className={cx(
          'yst-absolute yst-left-1/2 yst-top-full yst-z-50 yst-mt-2 yst-w-80 yst--translate-x-1/2 yst-transform',
          {
            'yst-block': isContentVisible,
            'yst-hidden': !isContentVisible,
            'yst-opacity-0':
              delay > 0 && isContentVisible && !hasAnimationStarted,
            'yst-animate-in yst-fade-in yst-slide-in-from-top-2 yst-duration-300':
              delay > 0 && hasAnimationStarted,
          },
          className,
        )}
        variant={variant}
        isVisible={isContentVisible}
        // No-op is passed because the visibility is superseded by direct control
        // in this wrapper. Passing a value here was causing the Popover to be
        // dismissed on mount.
        setIsVisible={() => {}}
        autoDismiss={0}
      >
        {children}
      </Toast>
    </div>
  );
};

export default Popover;
